export type TokenStore = { get(): string | null; set(t: string | null): void };

/**
 * The message a PERSON sees when a request fails.
 *
 * The API writes careful sentences — "This invite link is invalid. Please ask
 * your advisor for a new one." Prefixing them with "API 404:" put a status code
 * in front of a person who has just been told their invitation does not work,
 * and told them nothing they could act on. The status is still on the error
 * object (`ApiError.status`) for anything that needs to branch on it.
 *
 * With no message from the server there is nothing honest to say about the
 * cause, so say what is true and what to do instead.
 */
function errorMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
    return (body as { message: string }).message;
  }
  return status >= 500
    ? 'Something went wrong at our end. Please try again in a moment.'
    : 'That did not work. Please check the link or try again.';
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown) {
    super(errorMessage(status, body));
    this.name = 'ApiError';
  }
}

export type ApiClient = {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
};

type Opts = { baseUrl?: string; tokens: TokenStore; onUnauthorized?: () => Promise<boolean> };

export function createApiClient({ baseUrl = '', tokens, onUnauthorized }: Opts): ApiClient {
  let refreshInFlight: Promise<boolean> | null = null;

  function refresh(): Promise<boolean> {
    if (!onUnauthorized) return Promise.resolve(false);
    if (!refreshInFlight) {
      refreshInFlight = onUnauthorized().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  async function request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = tokens.get();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const res = await fetch(baseUrl + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && onUnauthorized && !retried) {
      const ok = await refresh();
      if (ok) return request<T>(method, path, body, true);
    }

    const text = await res.text();
    const parsed = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, parsed ?? text);
    return parsed as T;
  }

  function safeJson(text: string): unknown {
    try { return JSON.parse(text); } catch { return text; }
  }

  return {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    patch: (path, body) => request('PATCH', path, body),
    del: (path) => request('DELETE', path),
  };
}
